import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
    Award,
    ArrowRight,
    Calendar,
    Check,
    Download,
    Loader2,
    Mail,
    TrendingUp,
    AlertCircle,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BAND_COLORS = {
    leading: { bg: "#08C020", text: "#FFFFFF", soft: "rgba(8,192,32,0.12)" },
    advancing: { bg: "#08C020", text: "#FFFFFF", soft: "rgba(8,192,32,0.10)" },
    developing: { bg: "#C9A646", text: "#FFFFFF", soft: "rgba(201,166,70,0.14)" },
    emerging: { bg: "#1A2E4F", text: "#FFFFFF", soft: "rgba(26,46,79,0.10)" },
};

const ReadinessGauge = ({ value, band }) => {
    // Semi-circular progress arc
    const size = 240;
    const stroke = 16;
    const radius = (size - stroke) / 2;
    const circumference = Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;
    const color = BAND_COLORS[band?.key]?.bg || "#08C020";

    return (
        <div className="relative flex flex-col items-center" data-testid="report-gauge">
            <svg width={size} height={size / 2 + 16} viewBox={`0 0 ${size} ${size / 2 + 16}`}>
                <path
                    d={`M ${stroke / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - stroke / 2} ${size / 2}`}
                    stroke="#E5E5E5"
                    strokeWidth={stroke}
                    fill="none"
                    strokeLinecap="round"
                />
                <path
                    d={`M ${stroke / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - stroke / 2} ${size / 2}`}
                    stroke={color}
                    strokeWidth={stroke}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{ transition: "stroke-dashoffset 1s ease-out" }}
                />
            </svg>
            <div className="-mt-16 text-center">
                <p
                    className="font-montserrat font-bold text-indigo_brand"
                    style={{ fontSize: "3.5rem", lineHeight: 1 }}
                >
                    {Math.round(value)}
                </p>
                <p className="font-poppins text-[11px] uppercase tracking-[0.2em] text-[#4A5568] mt-1">
                    HUMAN+ Readiness
                </p>
            </div>
        </div>
    );
};

const PillarBar = ({ pillar }) => {
    return (
        <div
            data-testid={`report-pillar-${pillar.slug}`}
            className="grid gap-3 sm:gap-5 sm:grid-cols-[1fr_2.5fr_auto] items-center"
        >
            <div>
                <p className="font-poppins font-medium text-green_brand text-[10px] tracking-[0.2em]">
                    {pillar.number}
                </p>
                <p className="font-raleway font-semibold text-indigo_brand text-sm sm:text-base mt-0.5">
                    {pillar.name}
                </p>
            </div>
            <div className="h-2.5 rounded-full bg-surface-border/60 overflow-hidden">
                <div
                    className="h-full rounded-full bg-green_brand transition-all duration-700 ease-out"
                    style={{ width: `${pillar.score}%` }}
                />
            </div>
            <p className="font-montserrat font-bold text-indigo_brand text-lg text-right tabular-nums">
                {Math.round(pillar.score)}
            </p>
        </div>
    );
};

const Report = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("t");
    const tokenQuery = token ? `?t=${encodeURIComponent(token)}` : "";
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resending, setResending] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/snapshot/${id}${tokenQuery}`)
            .then((res) => setReport(res.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Report not found."))
            .finally(() => setLoading(false));
    }, [id, tokenQuery]);

    const resendPdf = async () => {
        setResending(true);
        try {
            const res = await axios.post(`${API}/snapshot/${id}/resend-pdf${tokenQuery}`);
            toast.success(`PDF sent to ${res.data.email}.`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not re-send the PDF.");
        } finally {
            setResending(false);
        }
    };

    if (loading) {
        return (
            <div className="ih-container py-32 flex items-center justify-center">
                <Loader2 className="animate-spin text-indigo_brand" size={28} />
            </div>
        );
    }
    if (err || !report) {
        return (
            <div className="ih-container py-24 text-center">
                <AlertCircle className="mx-auto text-indigo_brand" size={36} />
                <h1 className="mt-4 font-montserrat font-bold text-indigo_brand text-2xl">
                    We couldn&rsquo;t load that report.
                </h1>
                <p className="mt-3 font-opensans text-[#4A5568]">
                    {typeof err === "string" ? err : "Please take the Snapshot again."}
                </p>
                <Link to="/snapshot" className="ih-btn-primary mt-8 inline-flex">
                    Take the Snapshot
                    <ArrowRight size={16} />
                </Link>
            </div>
        );
    }

    const bandColor = BAND_COLORS[report.band.key] || BAND_COLORS.advancing;
    const completedDate = new Date(report.completed_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return (
        <div data-testid="report-page" className="bg-white">
            {/* Header */}
            <section className="bg-indigo_brand text-white">
                <div className="ih-container py-14 md:py-20">
                    <p className="ih-eyebrow" style={{ color: "#08C020" }}>
                        HUMAN+ Executive Report
                    </p>
                    <h1 className="mt-3 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-[3.25rem] tracking-tight leading-[1.05] max-w-3xl">
                        {report.lead.organization_name}
                    </h1>
                    <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-opensans text-white/75 text-sm">
                        <span>
                            Prepared for{" "}
                            <span className="text-white font-semibold">
                                {report.lead.first_name} {report.lead.last_name}
                            </span>
                            , {report.lead.job_title}
                        </span>
                        <span>{report.lead.industry}</span>
                        <span>{report.lead.organization_size} employees</span>
                        <span>Completed {completedDate}</span>
                    </div>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <a
                            href={`${API}/snapshot/${id}/pdf${tokenQuery}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="report-download-pdf"
                            className="ih-btn-primary"
                        >
                            <Download size={16} />
                            Download One-Page PDF
                        </a>
                        <button
                            type="button"
                            onClick={resendPdf}
                            disabled={resending}
                            data-testid="report-resend-pdf"
                            className="font-poppins font-medium text-sm uppercase tracking-[0.12em] text-white border border-white/40 hover:bg-white/10 px-7 py-[0.9rem] rounded-lg inline-flex items-center gap-2 transition disabled:opacity-60"
                        >
                            {resending ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
                            {resending ? "Sending…" : "Re-send PDF to my inbox"}
                        </button>
                    </div>
                </div>
            </section>

            {/* Readiness + Band */}
            <section className="ih-section bg-surface">
                <div className="ih-container">
                    <div className="ih-card p-8 md:p-12 shadow-medium grid gap-10 lg:grid-cols-[auto_1fr] lg:items-center">
                        <ReadinessGauge value={report.overall} band={report.band} />
                        <div>
                            <span
                                data-testid="report-band-label"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-poppins font-medium text-[11px] uppercase tracking-[0.18em]"
                                style={{
                                    background: bandColor.soft,
                                    color: bandColor.bg,
                                }}
                            >
                                <Award size={14} />
                                {report.band.label} · {report.band.min}–{report.band.max}
                            </span>
                            <h2 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl sm:text-3xl lg:text-4xl leading-tight">
                                {report.band.headline}
                            </h2>
                            <p
                                data-testid="report-executive-summary"
                                className="mt-5 font-opensans text-[#4A5568] text-base leading-relaxed max-w-3xl"
                            >
                                {report.executive_summary}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Six-pillar scorecard */}
            <section className="ih-section bg-white">
                <div className="ih-container">
                    <p className="ih-eyebrow">Scorecard</p>
                    <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl tracking-tight">
                        Your Six-Pillar HUMAN+ Scorecard
                    </h2>
                    <p className="mt-4 font-opensans text-[#4A5568] text-base max-w-2xl leading-relaxed">
                        Each pillar is scored 0–100 based on your responses across four
                        diagnostic statements. Higher scores indicate greater organizational readiness.
                    </p>

                    <div className="mt-10 ih-card p-8 md:p-10 space-y-7">
                        {report.pillar_scores.map((p) => (
                            <PillarBar key={p.slug} pillar={p} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Strengths & Growth */}
            <section className="ih-section bg-surface">
                <div className="ih-container grid gap-6 lg:grid-cols-2">
                    <div className="ih-card p-8 md:p-10" data-testid="report-strengths">
                        <span className="inline-flex w-10 h-10 items-center justify-center rounded-lg bg-green_brand/15">
                            <Sparkles size={20} className="text-green_brand" />
                        </span>
                        <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                            Strengths
                        </h3>
                        <p className="mt-2 font-opensans text-[#4A5568] text-sm">
                            The two pillars carrying your organization right now.
                        </p>
                        <ul className="mt-6 space-y-4">
                            {report.strengths.map((s) => (
                                <li key={s.slug} className="flex items-center justify-between gap-4 pb-4 border-b border-surface-border/60 last:border-0">
                                    <div className="flex items-start gap-3">
                                        <Check size={18} className="text-green_brand mt-1 shrink-0" />
                                        <p className="font-raleway font-semibold text-indigo_brand">
                                            {s.name}
                                        </p>
                                    </div>
                                    <p className="font-montserrat font-bold text-green_brand text-xl tabular-nums">
                                        {Math.round(s.score)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="ih-card p-8 md:p-10" data-testid="report-growth">
                        <span className="inline-flex w-10 h-10 items-center justify-center rounded-lg bg-gold_brand/15">
                            <TrendingUp size={20} className="text-gold_brand" />
                        </span>
                        <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                            Growth Opportunities
                        </h3>
                        <p className="mt-2 font-opensans text-[#4A5568] text-sm">
                            Where focused investment will move the needle fastest.
                        </p>
                        <ul className="mt-6 space-y-4">
                            {report.growth_opportunities.map((g) => (
                                <li key={g.slug} className="flex items-center justify-between gap-4 pb-4 border-b border-surface-border/60 last:border-0">
                                    <div className="flex items-start gap-3">
                                        <ArrowRight size={18} className="text-gold_brand mt-1 shrink-0" />
                                        <p className="font-raleway font-semibold text-indigo_brand">
                                            {g.name}
                                        </p>
                                    </div>
                                    <p className="font-montserrat font-bold text-gold_brand text-xl tabular-nums">
                                        {Math.round(g.score)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Personalized Interpretation */}
            <section className="ih-section bg-white">
                <div className="ih-container grid gap-12 lg:grid-cols-[1fr_2fr]">
                    <div>
                        <p className="ih-eyebrow">Interpretation</p>
                        <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl tracking-tight leading-tight">
                            What this means for your organization.
                        </h2>
                    </div>
                    <div data-testid="report-interpretation">
                        <p className="font-opensans text-indigo_brand text-lg leading-relaxed">
                            {report.interpretation}
                        </p>
                        <blockquote className="mt-8 ih-quote text-lg leading-snug border-l-4 border-green_brand pl-6">
                            Your organization&rsquo;s clearest strengths are{" "}
                            <span className="not-italic font-semibold text-indigo_brand">
                                {report.strengths[0].name}
                            </span>{" "}
                            and{" "}
                            <span className="not-italic font-semibold text-indigo_brand">
                                {report.strengths[1].name}
                            </span>
                            . The highest-leverage focus areas for the next 90 days are{" "}
                            <span className="not-italic font-semibold text-indigo_brand">
                                {report.growth_opportunities[0].name}
                            </span>{" "}
                            and{" "}
                            <span className="not-italic font-semibold text-indigo_brand">
                                {report.growth_opportunities[1].name}
                            </span>
                            .
                        </blockquote>
                    </div>
                </div>
            </section>

            {/* Recommendation CTA */}
            <section className="ih-section bg-indigo_brand text-white">
                <div className="ih-container">
                    <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-center">
                        <div>
                            <p className="ih-eyebrow" style={{ color: "#08C020" }}>
                                Recommendation
                            </p>
                            <h2 className="mt-3 font-montserrat font-bold text-white text-3xl sm:text-4xl lg:text-[2.65rem] leading-tight tracking-tight">
                                {report.recommendation}
                            </h2>
                            <p className="mt-5 font-opensans text-white/80 text-base leading-relaxed max-w-2xl">
                                The HUMAN+ Foundations Experience™ takes you from
                                insight to action — a guided program built directly on
                                the framework you just assessed against.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3">
                            {report.primary_cta === "foundations" ? (
                                <>
                                    <Link
                                        to="/foundations"
                                        data-testid="report-cta-foundations"
                                        className="ih-btn-primary"
                                    >
                                        Register for Foundations™
                                        <ArrowRight size={16} />
                                    </Link>
                                    <Link
                                        to="/discovery"
                                        data-testid="report-cta-discovery"
                                        className="font-poppins font-medium text-sm uppercase tracking-[0.12em] text-white border border-white/40 hover:bg-white/10 px-7 py-[0.9rem] rounded-lg inline-flex items-center gap-2 transition justify-center"
                                    >
                                        <Calendar size={16} />
                                        Schedule a Discovery Call
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/discovery"
                                        data-testid="report-cta-discovery"
                                        className="ih-btn-primary"
                                    >
                                        <Calendar size={16} />
                                        Schedule a Discovery Call
                                    </Link>
                                    <Link
                                        to="/foundations"
                                        data-testid="report-cta-foundations"
                                        className="font-poppins font-medium text-sm uppercase tracking-[0.12em] text-white border border-white/40 hover:bg-white/10 px-7 py-[0.9rem] rounded-lg inline-flex items-center gap-2 transition justify-center"
                                    >
                                        Foundations Experience™
                                        <ArrowRight size={16} />
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Report;
